import React, { useContext, useState } from 'react';

import { signIn, confirmSignIn, fetchAuthSession } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as MicrosoftIcon } from '../../../../../../assets/icons/v3/microsoft-icon.svg';
import StyledCircularProgress from '../../../../../../shared/components/StyledCircularProgress/StyledCircularProgress';
import {
  FIREBASE_MICROSOFT,
  LOGIN_PROVIDER_MICROSOFT,
  MICROSOFT_SIGN_UP_ROUTE
} from '../../../../../../shared/constants/auth';
import {
  ASSOCIATED_METHOD,
  CLICK_ACTION,
  ENTER_EMAIL,
  MICROSOFT_METHOD,
  MICROSOFT_PROVIDER
} from '../../../../../../shared/constants/tagManager';
import { SessionContext } from '../../../../../../shared/context/auth/authContext';
import Users from '../../../../../../shared/services/api/users/users';
import FirebaseService from '../../../../../../shared/services/firebase/firebase';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_AUTH } from '../../../../../../shared/translations/auth';
import { addProviderToEmail } from '../../../../../../shared/utils/auth/auth';

interface MicrosoftButtonProps {
  gtmTag: typeof ASSOCIATED_METHOD | typeof ENTER_EMAIL;
}

const MicrosoftButton = ({ gtmTag }: MicrosoftButtonProps) => {
  const [loading, setLoading] = useState(false);
  const { customLogin, resetError } = useContext(SessionContext);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleGetUserFirebase = async () => {
    try {
      setLoading(true);
      resetError('');
      TagManagerService.pushEvent(CLICK_ACTION, gtmTag, MICROSOFT_METHOD);
      const credentials = await FirebaseService.getFirebaseUserFromProvider(FIREBASE_MICROSOFT);
      const token = await credentials.user.getIdToken();
      const userEmail = credentials.user.email ?? '';
      await handleCreateMicrosoftUser(token, addProviderToEmail(userEmail, LOGIN_PROVIDER_MICROSOFT));
    } catch (error) {
      if (
        error &&
        'code' in (error as Record<string, unknown>) &&
        (error as any).code === 'auth/popup-closed-by-user'
      ) {
        setLoading(false);
        return;
      }
      setLoading(false);
      resetError('firebase error');
    }
  };

  const handleCreateMicrosoftUser = async (idToken: string, email: string) => {
    const response = await Users.checkEmail(email);
    if (!response.verified) {
      navigate(MICROSOFT_SIGN_UP_ROUTE, { state: { email, idToken } });
    } else {
      await handleLogin(idToken, email);
    }
  };

  const handleLogin = async (idToken: string, email: string) => {
    try {
      setLoading(true);
      const signInResult = await signIn({ username: email, options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } });
      if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        await confirmSignIn({ challengeResponse: idToken });
        const session = await fetchAuthSession();
        const idTok = session.tokens?.idToken;
        let adminEmail = '';
        let userId = '';
        if (idTok && typeof idTok === 'object' && idTok.payload) {
          adminEmail = typeof idTok.payload.email === 'string' ? idTok.payload.email : '';
          userId = typeof idTok.payload.sub === 'string' ? idTok.payload.sub : '';
        }
        await customLogin(
          { attributes: { email: adminEmail, sub: userId } },
          LOGIN_PROVIDER_MICROSOFT,
          MICROSOFT_PROVIDER
        );
      }
    } catch {
      resetError('custom sign in error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button className="login-button" onClick={handleGetUserFirebase}>
      {loading ? (
        <StyledCircularProgress />
      ) : (
        <div className="button-info">
          <MicrosoftIcon />
          <span className="description">{t(TRANSLATIONS_AUTH.loginCard.microsoftButtonText)}</span>
        </div>
      )}
    </button>
  );
};

export default MicrosoftButton;
